import formatCreditCardPaymentRequest from './formatCreditCardPaymentRequest';

/**
 * Extracts token payload from tokenized card map or single object
 * @param {Object|null} tokenizedCardMapResult - Tokenized card data
 * @param {string} targetKey - Target card key
 * @returns {Object|null} Token payload or null
 */
const extractTokenPayload = (tokenizedCardMapResult, targetKey) => {
  if (!tokenizedCardMapResult) {
    return null;
  }

  const isObject = typeof tokenizedCardMapResult === 'object'
    && !Array.isArray(tokenizedCardMapResult);

  if (!isObject) {
    return tokenizedCardMapResult;
  }

  // Check if it's a map (has the targetKey) or a single tokenized card object
  if (targetKey in tokenizedCardMapResult) {
    return tokenizedCardMapResult[targetKey];
  }

  return tokenizedCardMapResult;
};

/**
 * Builds credit card payment object for booking/quote
 * @param {Object} params - Parameters for building payment
 * @param {boolean} params.doIncludeCC - Whether to include credit card
 * @param {boolean} params.doUseSavedCardForBooking - Whether using saved card
 * @param {Object} params.creditCards - Available credit cards
 * @param {Object} params.tokenizedCardMapResult - Tokenized card data (can be map or single object)
 * @param {boolean} params.isLoggedIn - User login status
 * @returns {Object|undefined} Formatted credit card payment or undefined
 */
export const buildCreditCardPayment = (params) => {
  const {
    doIncludeCC,
    doUseSavedCardForBooking,
    creditCards,
    tokenizedCardMapResult,
    isLoggedIn,
  } = params;

  if (!doIncludeCC || doUseSavedCardForBooking) {
    return undefined;
  }

  const cardEntries = Object.entries(creditCards || {});
  if (!cardEntries.length) {
    return undefined;
  }

  // Find picked card or use first card
  const foundEntry = cardEntries.find(([, card]) => card.isPicked) || cardEntries[0];
  if (!foundEntry) {
    return undefined;
  }

  const [targetKey, targetCard] = foundEntry;
  if (!targetCard) {
    return undefined;
  }

  // Extract token payload from tokenized result
  const tokenPayload = extractTokenPayload(tokenizedCardMapResult, targetKey);

  return formatCreditCardPaymentRequest(
    tokenPayload || targetCard,
    targetCard,
    isLoggedIn,
  );
};

/**
 * Builds credit card payments array for booking (multiple cards)
 * @param {Object} params - Parameters for building payments
 * @param {boolean} params.doIncludeCC - Whether to include credit cards
 * @param {boolean} params.doUseSavedCardForBooking - Whether using saved card
 * @param {Object} params.creditCards - Available credit cards
 * @param {Object} params.tokenizedCardMap - Tokenized card data map
 * @param {boolean} params.isLoggedIn - User login status
 * @returns {Array|undefined} Array of formatted credit card payments or undefined
 */
export const buildCreditCardPayments = (params) => {
  const {
    doIncludeCC,
    doUseSavedCardForBooking,
    creditCards,
    tokenizedCardMap,
    isLoggedIn,
  } = params;

  if (!doIncludeCC || doUseSavedCardForBooking) {
    return undefined;
  }

  // Handle tokenized cards
  if (tokenizedCardMap && Object.keys(tokenizedCardMap).length > 0) {
    const pickedEntries = Object.entries(creditCards)
      .filter(([, card]) => card.isPicked);
    const cardsToUse = pickedEntries.length > 0
      ? pickedEntries
      : Object.entries(creditCards).slice(0, 1);

    if (cardsToUse.length === 0) {
      return undefined;
    }

    return cardsToUse.map(([index, card]) => {
      const tokenPayload = tokenizedCardMap[index];
      if (!tokenPayload) {
        throw new Error('Missing tokenized data for one of the credit cards.');
      }
      return formatCreditCardPaymentRequest(
        tokenPayload,
        card,
        isLoggedIn,
      );
    });
  }

  // Handle non-tokenized cards
  const pickedCards = Object.values(creditCards)
    .filter((card) => card.isPicked);

  if (pickedCards.length === 0) {
    return undefined;
  }

  return pickedCards.map((card) => formatCreditCardPaymentRequest(card, card, isLoggedIn));
};

export default buildCreditCardPayment;
